import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Download } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp } from "../components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { submitCatalogRequest } from "../lib/api";
import { PRODUCT_CATEGORIES } from "../data/catalog";
import { toast, Toaster } from "sonner";

const FAQS = [
  { q: "How much do NUVUN modular structures cost?", a: "Apple Pods start from ₹8.5 L, prefab homes range ₹12 L – ₹95 L, capsules ₹14 L – ₹75 L, and resort inventory is quoted per key (₹18 L – ₹45 L / key). Prices exclude GST (18%), transport and foundation. Every quote is itemized — no surprises." },
  { q: "How fast can my project be delivered?", a: "80–90% of every structure is completed inside our factory. Pods install in 10–21 days, homes in 45–90 days, and full resort clusters in 90–150 days — up to 45% faster than conventional construction." },
  { q: "Are prefab structures legally approved in India?", a: "Yes. NUVUN structures comply with local municipal bylaws and zoning regulations. We provide structural drawings and documentation for approvals, and several formats can be classed as temporary/movable structures depending on the model." },
  { q: "What materials and warranty do you offer?", a: "Light gauge steel and precast systems, Rockwool insulation, weather-proof membranes, UPVC glazing and branded fittings — backed by a 20-year structural & leakage warranty with a 50+ year design life." },
  { q: "Can units be customized or relocated?", a: "Every model is configurable across layout, façade, roofing and interior packages. Pod and capsule formats on pre-cast foundations can be dismantled and relocated." },
  { q: "Do you build complete resorts?", a: "Yes — from masterplanning to handover. Our hospitality team delivers room clusters, common areas and back-of-house as phased, revenue-aligned packages. See the Hospitality page for ROI models." },
];

export default function Resources() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "" });
  const [submitting, setSubmitting] = useState(false);

  const handle = (f) => (e) => setForm((s) => ({ ...s, [f]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitCatalogRequest({ ...form, interest: form.interest || "Full NUVUN brochure" });
      toast.success("Request received — the catalogue is on its way to your inbox.");
      setForm({ name: "", email: "", phone: "", interest: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Toaster position="top-center" richColors />

      {/* Header + brochure form */}
      <section className="pt-[150px] pb-16 relative overflow-hidden" data-testid="resources-header">
        <div className="absolute top-[-160px] left-[-160px] w-[480px] h-[480px] rounded-full bg-[#D9A441]/[0.05] blur-[110px] pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <div className="eyebrow eyebrow-dash text-[#D9A441] mb-5">Resources</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3]">
                Everything you need <span className="italic text-[#F2EDE3]/70">to decide.</span>
              </h1>
              <p className="mt-6 text-sm md:text-base text-[#F2EDE3]/60 leading-relaxed max-w-md">
                Brochures, per-collection catalogues, delivered case studies and honest
                answers on pricing, timelines and approvals.
              </p>
              <div className="mt-8 flex items-center gap-6">
                <div className="panel px-5 py-4 text-center">
                  <div className="font-condensed text-2xl text-[#D9A441]">39</div>
                  <div className="text-[9px] uppercase tracking-[0.14em] text-[#F2EDE3]/50 mt-1">Models</div>
                </div>
                <div className="panel px-5 py-4 text-center">
                  <div className="font-condensed text-2xl text-[#D9A441]">6</div>
                  <div className="text-[9px] uppercase tracking-[0.14em] text-[#F2EDE3]/50 mt-1">Collections</div>
                </div>
                <div className="panel px-5 py-4 text-center">
                  <div className="font-condensed text-2xl text-[#D9A441]">2026</div>
                  <div className="text-[9px] uppercase tracking-[0.14em] text-[#F2EDE3]/50 mt-1">Edition</div>
                </div>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <form onSubmit={submit} data-testid="brochure-form" className="panel p-7 md:p-9">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-full border border-[#D9A441]/50 flex items-center justify-center">
                  <Download className="w-4 h-4 text-[#D9A441]" />
                </span>
                <div>
                  <div className="font-display text-xl text-[#F2EDE3]">Download the Brochure</div>
                  <div className="text-xs text-[#F2EDE3]/50 mt-0.5">2026 edition · designs, specs & starting prices</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="brochure-name" required value={form.name} onChange={handle("name")} placeholder="Full name *" className="field" />
                <input data-testid="brochure-email" required type="email" value={form.email} onChange={handle("email")} placeholder="Email *" className="field" />
                <input data-testid="brochure-phone" value={form.phone} onChange={handle("phone")} placeholder="Phone" className="field" />
                <select data-testid="brochure-interest" value={form.interest} onChange={handle("interest")} className="field">
                  <option value="">Interested in (optional)</option>
                  {PRODUCT_CATEGORIES.map((c) => (
                    <option key={c.slug} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <button type="submit" disabled={submitting} data-testid="brochure-submit" className="btn-gold w-full mt-5 disabled:opacity-60">
                {submitting ? "Sending…" : "Send Me The Catalogue"} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-3 text-[11px] text-[#F2EDE3]/40">Delivered by email within one business day. No spam, ever.</p>
            </form>
          </FadeUp>
        </div>
      </section>

      {/* Catalogues per collection */}
      <section id="catalogues" className="py-16 border-t border-[#D9A441]/12" data-testid="resources-catalogues">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <FadeUp>
            <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">Catalogues</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F2EDE3]">Per-collection spec sheets.</h2>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRODUCT_CATEGORIES.map((c, i) => (
              <FadeUp key={c.slug} delay={i * 0.05}>
                <Link
                  to={`/products/category/${c.slug}`}
                  data-testid={`catalogue-${c.slug}`}
                  className="group panel flex items-center gap-5 p-6 hover:border-[#D9A441]/50 transition-colors"
                >
                  <span className="w-12 h-12 rounded-md overflow-hidden shrink-0">
                    <img src={c.hero_image} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </span>
                  <span className="flex-1">
                    <span className="block font-display text-lg text-[#F2EDE3] group-hover:text-[#D9A441] transition-colors">{c.name}</span>
                    <span className="block text-xs text-[#F2EDE3]/50 mt-1">Collection {c.num} · spec sheet & pricing</span>
                  </span>
                  <FileText className="w-4 h-4 text-[#D9A441]" />
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faqs" className="py-16 pb-24 border-t border-[#D9A441]/12" data-testid="resources-faqs">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <FadeUp className="text-center">
            <div className="eyebrow text-[#D9A441] mb-4">FAQs</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F2EDE3]">Honest answers, upfront.</h2>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="panel !border-[#D9A441]/18 px-6 data-[state=open]:!border-[#D9A441]/45">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-display text-base md:text-lg text-[#F2EDE3] hover:no-underline hover:text-[#D9A441] text-left">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#F2EDE3]/60 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-10 text-center">
            <Link to="/contact" data-testid="faq-contact-cta" className="btn-ghost">
              Still curious? Talk to us <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
